import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, AlertTriangle, ExternalLink, Clock, MessageCircle, Shield, Users } from 'lucide-react';
import { emailAPI, hubspotAPI } from '../../services/api';
import Alert from '../../components/common/Alert';
import Button from '../../components/common/Button';
import ThreadTimeline from './components/ThreadTimeline';

const SecurityBadge = ({ status, text }) => {
  const getStatusStyles = () => {
    switch (status) {
      case 'verified':
        return 'bg-spring-green/10 text-spring-green border-spring-green/20';
      case 'warning':
        return 'bg-celadon/10 text-violet border-celadon/20';
      default:
        return 'bg-bittersweet/10 text-bittersweet border-bittersweet/20';
    }
  };

  return (
    <div className={`flex items-center px-3 py-1 rounded-pill text-sm font-medium border ${getStatusStyles()}`}>
      {status === 'verified' ? (
        <CheckCircle className="w-4 h-4 mr-1" />
      ) : (
        <AlertTriangle className="w-4 h-4 mr-1" />
      )}
      {text}
    </div>
  );
};

export default function EmailVerification() {
  const { scode: urlScode } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const urlEmail = searchParams.get('email');

  const [status, setStatus] = useState('pending');
  const [error, setError] = useState('');
  const [emailData, setEmailData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLeadSubmitting, setIsLeadSubmitting] = useState(false);
  const [leadSuccess, setLeadSuccess] = useState(false);
  const [formData, setFormData] = useState({
    scode: urlScode || '',
    recipientEmail: urlEmail || ''
  });

  // Function to handle closing the window with fallbacks
  const handleCloseWindow = () => {
    try {
      // First try the standard window.close()
      window.close();
      
      // If we're still here after a short delay, the window didn't close
      // This could be because it wasn't opened by JavaScript
      setTimeout(() => {
        // Check if window is still open
        if (!window.closed) {
          // Try to navigate back if there's history
          if (window.history.length > 1) {
            window.history.back();
          } else {
            // Redirect to a known page or show a message
            navigate('/');
          }
        }
      }, 300);
    } catch (e) {
      console.log('Could not close window, attempting to redirect');
      navigate('/');
    }
  };

  // Only pre-fill the form fields but don't auto-verify
  useEffect(() => {
    if (urlScode) {
      setFormData(prev => ({
        ...prev,
        scode: urlScode
      }));
      
      // Extract additional parameters if available
      const emailId = searchParams.get('id');
      const token = searchParams.get('token');
      const threadScode = searchParams.get('thread');
      
      if (emailId) {
        setFormData(prev => ({
          ...prev,
          emailId,
          token,
          threadScode
        }));
        
        console.log('Enhanced verification parameters found:', {
          scode: urlScode,
          emailId,
          hasToken: !!token,
          hasThreadScode: !!threadScode
        });
      }
    }
    
    if (urlEmail) {
      setFormData(prev => ({
        ...prev,
        recipientEmail: urlEmail
      }));
    }
  }, [urlScode, urlEmail, searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString(undefined, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'Invalid date';
    }
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHours = Math.floor(diffMin / 60);
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffDays > 7) {
        return formatDate(dateString);
      } else if (diffDays > 1) {
        return `${diffDays} days ago`;
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffHours > 1) {
        return `${diffHours} hours ago`;
      } else if (diffMin > 1) {
        return `${diffMin} minutes ago`;
      } else {
        return 'Just now';
      }
    } catch {
      return formatDate(dateString);
    }
  };

  const handleLeadInterest = async () => {
    if (!formData.recipientEmail) {
      setError('Email address is required');
      return;
    }
    
    setIsLeadSubmitting(true);
    try {
      await hubspotAPI.createContact(
        formData.recipientEmail, 
        'Email Verification Page'
      );
      
      setLeadSuccess(true);
      setError(''); // Clear any previous errors
      
      // Optional: Track the successful lead submission
      console.log('Lead submitted successfully:', formData.recipientEmail);
      
    } catch (err) {
      console.error('Lead submission error:', err);
      setError(err.response?.data?.message || 'Failed to submit your interest. Please try again.');
      setLeadSuccess(false);
    } finally {
      setIsLeadSubmitting(false);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    
    // Require both scode and recipient email
    if (!formData.scode) {
      setError('Please enter a verification code');
      return;
    }
    
    if (!formData.recipientEmail) {
      setError('Please enter your email address');
      return;
    }
  
    setIsSubmitting(true);
    setError('');
  
    try {
      // Use emailAPI service to verify the email with enhanced parameters
      const verificationData = {
        scode: formData.scode,
        recipientEmail: formData.recipientEmail
      };
      
      // Add enhanced parameters if available
      if (formData.emailId && formData.token) {
        verificationData.emailId = formData.emailId;
        verificationData.token = formData.token;
      }
      
      if (formData.threadScode) {
        verificationData.thread = formData.threadScode;
      }
      
      const response = await emailAPI.verifyEmail(verificationData);
      
      // Check if the response structure is valid
      if (!response?.data?.data) {
        throw new Error('Invalid response format from the server');
      }
      
      // Update UI with response data
      setEmailData(response.data.data);
      setStatus('success');
      
      // Update URL to include the recipient email (if not already there)
      if (!urlEmail && formData.recipientEmail) {
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.set('email', formData.recipientEmail);
        
        // Use replace to avoid adding to browser history
        navigate({
          pathname: location.pathname,
          search: newSearchParams.toString()
        }, { replace: true });
      }
    } catch (error) {
      console.error('Verification error:', error);
      
      // Extract meaningful error message from the response
      let errorMessage = 'Verification failed. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Handle specific error codes
      if (error.response?.status === 403) {
        errorMessage = 'The email address you entered does not match any recipient of this message.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Invalid verification code. Please check and try again.';
      }
      
      setError(errorMessage);
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Simple thread summary for the main view
  const ThreadSummary = ({ threadActivity }) => {
    if (!threadActivity || !threadActivity.hasReplies) return null;
    
    const { totalMessages, participants, threadDuration } = threadActivity;
    
    return (
      <div className="flex justify-between items-center py-3 border-b">
        <span className="font-medium text-midnight flex items-center font-heading">
          <MessageCircle className="h-4 w-4 mr-1" />
          Conversation
        </span>
        <div className="text-right">
          <div className="text-ebony">
            {totalMessages} message{totalMessages !== 1 ? 's' : ''} · {participants.length} participant{participants.length !== 1 ? 's' : ''}
          </div>
          {threadDuration && (
            <div className="text-xs text-violet mt-1">
              Conversation spans {threadDuration}
            </div>
          )}
        </div>
      </div>
    );
  };
  
  const SuccessContent = () => (
    <div className="space-y-6">
      <div className="flex justify-center">
        <CheckCircle className="h-12 w-12 text-spring-green" />
      </div>
      <div className="text-center">
        <h2 className="text-2xl font-bold text-midnight font-heading">Email Verified Successfully</h2>
        <p className="text-ebony mt-2">This email has been verified as authentic.</p>
      </div>
  
      <div className="space-y-4">
        <div className="bg-white rounded-xl border border-ebony/20 p-6 space-y-4">
          <div className="flex justify-between items-start pb-4 border-b">
            <span className="font-medium text-midnight font-heading">From</span>
            <div className="text-right">
              <div className="font-medium">{emailData.sender?.name}</div>
              <div className="text-sm text-ebony">{emailData.sender?.email}</div>
              {emailData.sender.organization?.name && (
                <div className="text-xs text-violet font-medium mt-1">
                  {emailData.sender.organization.name}
                </div>
              )}
              <div className="text-xs text-ebony mt-1 flex items-center justify-end">
                Domain: {emailData.sender.domain}
                {emailData.sender.domainInfo?.verified && (
                  <CheckCircle className="h-3 w-3 text-spring-green ml-1" />
                )}
              </div>
            </div>
          </div>
  
          {emailData.subject && (
            <div className="flex justify-between items-center py-3 border-b">
              <span className="font-medium text-midnight font-heading">Subject</span>
              <span className="text-ebony">{emailData.subject}</span>
            </div>
          )}
  
          <div className="flex justify-between items-center py-3 border-b">
            <span className="font-medium text-midnight font-heading">Sent</span>
            <span className="text-ebony">{formatDate(emailData.sentAt)}</span>
          </div>
  
          {/* Show thread summary if available */}
          {emailData.threadActivity && emailData.threadActivity.hasReplies && (
            <ThreadSummary threadActivity={emailData.threadActivity} />
          )}
  
          <div className="flex justify-between items-center pt-3">
            <span className="font-medium text-midnight font-heading">Verified</span>
            <span className="text-ebony">{formatDate(emailData.verifiedAt)}</span>
          </div>
        </div>
  
        {/* Enhanced thread timeline display */}
        {emailData.threadActivity && emailData.threadActivity.hasReplies && (
          <ThreadTimeline 
            threadActivity={emailData.threadActivity} 
            currentEmailId={emailData.id}
          />
        )}
  
        {emailData.isReply && !emailData.threadActivity?.hasReplies && (
          <div className="bg-white rounded-xl border border-ebony/20 p-4 flex items-start">
            <Clock className="h-5 w-5 text-violet mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-midnight font-heading">This is a reply in a conversation</h4>
              <p className="text-sm text-ebony">
                This email is part of an ongoing conversation.
              </p>
            </div>
          </div>
        )}
  
        {!leadSuccess ? (
          <div className="bg-violet/5 rounded-xl p-6 text-center">
            <h3 className="font-medium text-violet mb-2 font-heading">Protect Your Own Emails</h3>
            <p className="text-sm text-ebony mb-4">
              Want to secure your organization's emails with Stickr?
            </p>
            <Button
              onClick={handleLeadInterest}
              variant="outline"
              className="w-full bg-white hover:bg-violet/5"
              disabled={isLeadSubmitting}
            >
              {isLeadSubmitting ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader className="animate-spin h-5 w-5" />
                  <span>Submitting...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <Shield className="h-5 w-5" />
                  <span>Get Started with Stickr</span>
                  <ExternalLink className="h-4 w-4" />
                </div>
              )}
            </Button>
          </div>
        ) : (
          <Alert type="success">
            Thanks for your interest! Our team will contact you soon.
          </Alert>
        )}
  
        <Button onClick={handleCloseWindow} className="w-full">
          Close Window
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet/5 to-white flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {/* Replace Shield icon and text with logo.svg */}
          <div className="flex items-center justify-center mb-8">
            <img src="/violetMidnightStickrLogo.svg" alt="Stickr Logo" className="h-6" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-ebony/20 p-8">
          {status === 'pending' && (
            <div className="space-y-6">
              <div className="bg-violet/5 border border-violet/10 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-5 w-5 text-violet" />
                  <h3 className="font-semibold text-violet font-heading">Secure Email Verification</h3>
                </div>
                <p className="text-sm text-ebony leading-relaxed">
                  This email was protected by Stickr to ensure its authenticity. 
                  Please enter the email address where you received this message
                  to verify the sender's identity and protect yourself from 
                  potential phishing attempts.
                </p>
              </div>

              <div className="text-center">
                <h2 className="text-2xl font-bold text-midnight font-heading">Verify Your Email</h2>
                <p className="mt-2 text-ebony">
                  Please enter your email address to verify this communication.
                </p>
              </div>

              <form onSubmit={handleVerification} className="space-y-4">
                <div>
                  <label htmlFor="scode" className="block text-sm font-medium text-midnight mb-1 font-heading">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    id="scode"
                    name="scode"
                    value={formData.scode}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 rounded-pill border-2 border-light-purple focus:border-sky-blue focus:ring focus:ring-sky-blue/20 bg-old-lace/30"
                    placeholder="Enter verification code"
                    required
                    readOnly={!!urlScode}
                  />
                </div>

                <div>
                  <label htmlFor="recipientEmail" className="block text-sm font-medium text-midnight mb-1 font-heading">
                    Your Email Address
                  </label>
                  <input
                    type="email"
                    id="recipientEmail"
                    name="recipientEmail"
                    value={formData.recipientEmail}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 rounded-pill border-2 border-light-purple focus:border-sky-blue focus:ring focus:ring-sky-blue/20 bg-old-lace/30"
                    placeholder="Enter your email address"
                    required
                  />
                  <p className="text-xs text-ebony mt-1">
                    Enter the email address where you received this message
                  </p>
                </div>

                {error && <Alert type="error">{error}</Alert>}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader className="animate-spin h-5 w-5" />
                      <span>Verifying...</span>
                    </div>
                  ) : (
                    'Verify Email'
                  )}
                </Button>
              </form>
            </div>
          )}

          {status === 'success' && emailData && <SuccessContent />}

          {status === 'error' && (
            <div className="space-y-6 text-center">
              <XCircle className="h-12 w-12 text-bittersweet mx-auto" />
              <h2 className="text-2xl font-bold text-midnight font-heading">Verification Failed</h2>
              <p className="text-bittersweet">{error}</p>
              <div className="flex gap-3 justify-center">
                <Button 
                  variant="outline" 
                  onClick={() => setStatus('pending')}
                  className="border-ebony/20 hover:bg-old-lace/50"
                >
                  Try Again
                </Button>
                <Button onClick={handleCloseWindow}>
                  Close Window
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="text-center text-sm text-ebony mt-8">
          <p>Protected by Stickr © {new Date().getFullYear()}</p>
        </div>
      </div>
    </div>
  );
}